import { useAppDispatch, useAppSelector } from "@/shared/lib/redux"
import React, { useState, useEffect } from "react"
import { SubmitHandler, useForm, Controller } from "react-hook-form"
import notify from "@/shared/lib/notify"
import { yupResolver } from "@hookform/resolvers/yup"
import { Modal } from "@/shared/ui/modal"
import { FormInput } from "@/shared/ui/form-input"
import { getErrorMessage } from "@/shared/consts/error-codes"
import { createBusinessCard as createBusinessCardThunk } from "../../model"
import { detectPaymentSystem } from "../../lib"
import { selectCommunityInfo } from "@/entities/community"
import { UnverifiedCommunityNotificationModal } from "../unverified-community-notification-modal"
import * as yup from "yup"
import { sha1 } from "js-sha1"

interface CreateBusinessCardFormValues {
    number: string
    expiryMonth: string
    expiryYear: string
    cvv: string
}

const createBusinessCardSchema = yup.object({
    number: yup.string()
        .required("Номер карты обязателен для заполнения")
        .matches(/^[\d\s]{13,19}$/, "Номер карты должен содержать от 13 до 19 цифр"),
    expiryMonth: yup.string()
        .required("Месяц обязателен для заполнения")
        .matches(/^(0[1-9]|1[0-2])$/, "Месяц должен быть от 01 до 12"),
    expiryYear: yup.string()
        .required("Год обязателен для заполнения")
        .matches(/^\d{2}$/, "Год должен состоять из 2 цифр"),
    cvv: yup.string()
        .required("CVV обязателен для заполнения")
        .matches(/^\d{3,4}$/, "CVV должен состоять из 3 или 4 цифр"),
})

interface CreateBusinessCardModalProps {
    isOpen: boolean
    onClose: () => void
    onCreate: () => void
}

export const CreateBusinessCardModal: React.FC<CreateBusinessCardModalProps> = ({ isOpen, onClose, onCreate }) => {
    const dispatch = useAppDispatch()
    const community = useAppSelector(selectCommunityInfo)
    const [paymentSystem, setPaymentSystem] = useState<string>("")
    const [showUnverifiedModal, setShowUnverifiedModal] = useState(false)

    const initialState: CreateBusinessCardFormValues = {
        number: "",
        expiryMonth: "",
        expiryYear: "",
        cvv: ""
    }

    const onSubmit: SubmitHandler<typeof initialState> = async (data) => {
        // Check if community is verified before allowing card creation
        if (!community?.is_verified) {
            setShowUnverifiedModal(true)
            return
        }

        await new Promise((res, rej) => {
            dispatch(createBusinessCardThunk({
                payment_system_type_id: 1,
                bank_type_id: 1,
                number: data.number.slice(-4),
                hash: sha1(data.number.split(' ').join('')).toUpperCase()
            }))
                .unwrap()
                .then(() => {
                    notify.success('Карта успешно добавлена')
                    reset()
                    setPaymentSystem("")
                    onCreate()
                    onClose()
                    res('')
                })
                .catch((e) => {
                    notify.error(getErrorMessage(e))
                    rej()
                })
        })
    }

    const {
        handleSubmit,
        register,
        watch,
        control,
        setValue,
        trigger,
        formState: { errors, isSubmitting },
        reset
    } = useForm({
        defaultValues: initialState,
        resolver: yupResolver(createBusinessCardSchema)
    })

    const cardNumber = watch("number")
    const expiryMonth = watch("expiryMonth")
    const expiryYear = watch("expiryYear")

    useEffect(() => {
        register("expiryMonth")
        register("expiryYear")
    }, [register])

    const expiryValue = React.useMemo(() => {
        if (!expiryMonth && !expiryYear) {
            return ""
        }

        if (expiryYear) {
            return `${expiryMonth}${expiryMonth?.length === 2 ? " / " : ""}${expiryYear}`
        }

        return expiryMonth
    }, [expiryMonth, expiryYear])

    const handleExpiryChange = (value: string) => {
        const cleaned = value.replace(/\D/g, "").slice(0, 4)
        let month = cleaned.slice(0, 2)
        const year = cleaned.slice(2, 4)

        if (month.length === 2) {
            const parsedMonth = parseInt(month, 10)

            if (parsedMonth === 0) {
                month = "01"
            } else if (parsedMonth > 12) {
                month = "12"
            }
        }

        setValue("expiryMonth", month, { shouldValidate: true, shouldDirty: true, shouldTouch: true })
        setValue("expiryYear", year, { shouldValidate: true, shouldDirty: true, shouldTouch: true })
    }

    const expiryError = errors["expiryMonth"] || errors["expiryYear"]
    
    useEffect(() => {
        if (cardNumber && cardNumber.replace(/\s/g, '').length >= 4) {
            const detected = detectPaymentSystem(cardNumber)
            setPaymentSystem(detected)
        } else {
            setPaymentSystem("")
        }
    }, [cardNumber])

    return (
        <>
            <Modal
                isOpen={isOpen}
                title="Добавить карту"
                onRequestClose={onClose}
            >
                <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3">
                    <div className="flex flex-col gap-[6px] pb-[8px] border-b-[1.5px] border-[#B9C9C6]">
                        <label className="font-semibold uppercase text-base text-[#828282]">
                            Платёжная система
                        </label>
                        <div className="text-lg text-color-dark">
                            {paymentSystem || "Определяется автоматически"}
                        </div>
                    </div>
                    <Controller
                        control={control}
                        name="number"
                        render={({ field }) => (
                            <div className={`flex flex-col gap-[6px] pb-[8px] border-b-[1.5px] border-[#B9C9C6] ${errors['number'] && 'border-color-danger'}`}>
                                <div className="flex justify-between w-full items-center">
                                    <label className="font-semibold uppercase text-base text-[#828282]">
                                        Номер карты
                                    </label>
                                    {errors['number'] &&
                                        <label className="text-base flex items-center text-color-danger">
                                            {errors['number']?.message}
                                        </label>
                                    }
                                </div>
                                <input 
                                    placeholder="0000 0000 0000 0000"
                                    maxLength={19}
                                    className={`w-full outline-0 text-lg [&::placeholder]:text-color-dark/70 [&::placeholder]:text-base outline-none text-color-dark`}
                                    value={field.value}
                                    onChange={(e) => {
                                        const cleaned = e.target.value.replace(/\s/g, '')
                                        const formatted = cleaned.match(/.{1,4}/g)?.join(' ') || cleaned
                                        field.onChange(formatted)
                                    }}
                                    onBlur={field.onBlur}
                                />
                            </div>
                        )}
                    />
                    <div className={`flex flex-col gap-[6px] pb-[8px] border-b-[1.5px] border-[#B9C9C6] ${expiryError && 'border-color-danger'}`}>
                        <div className="flex justify-between w-full items-center">
                            <label className="font-semibold uppercase text-base text-[#828282]">
                                Срок действия
                            </label>
                            {expiryError &&
                                <label className="text-base flex items-center text-color-danger">
                                    {expiryError?.message}
                                </label>
                            }
                        </div>
                        <input
                            placeholder="MM / YY"
                            maxLength={7}
                            inputMode="numeric"
                            className="w-full outline-0 text-lg [&::placeholder]:text-color-dark/70 [&::placeholder]:text-base outline-none text-color-dark"
                            value={expiryValue}
                            onChange={(e) => handleExpiryChange(e.target.value)}
                            onBlur={() => trigger(["expiryMonth", "expiryYear"])}
                        />
                    </div>
                    <FormInput
                        register={register}
                        label={"CVV"}
                        id={"cvv"}
                        error={errors['cvv']}
                        required={true}
                        placeholder="000"
                        type="password"
                        maxLength={4}
                    />
                    <div className="w-full flex gap-3">
                        <button onClick={onClose} type="button" className="form_cancel_button">
                            Отменить
                        </button>
                        <button disabled={isSubmitting} className="form_handle_button" type="submit">
                            Сохранить
                        </button>
                    </div>
                </form>
            </Modal>
            <UnverifiedCommunityNotificationModal
                isOpen={showUnverifiedModal}
                onClose={() => setShowUnverifiedModal(false)}
            />
        </>
    )
}

